import { FC } from 'react';

import { twMerge } from 'tailwind-merge';

import { CopyArea } from '@/features/copy-area';

import { CurrencyIcon } from '@/entities/currency';
import { useRequisitesStore } from '@/entities/currency-account/model/store';

import { ICurrencyAccount } from '@/shared/lib';
import { ScreenCard, Typography } from '@/shared/ui';

import { getAccountInfoForCopy } from '../lib/get-account-info-for-copy';

interface InfoItemProps {
  value: string | number;
  title: string;
}

const InfoItem = ({ value, title }: InfoItemProps) => {
  return (
    <CopyArea
      className="w-full justify-between gap-x-4 whitespace-normal !py-2 text-start [&_svg]:size-6"
      copyText={value}
      successCopyText={`${title} copied`}
    >
      <div className="flex flex-col gap-y-1">
        <Typography variant="13-r" color="text-base-100">
          {title}
        </Typography>

        <Typography variant="16-r">{value}</Typography>
      </div>
    </CopyArea>
  );
};

export const AccountInfo: FC<{
  account: ICurrencyAccount;
  isModal?: boolean;
}> = ({ account, isModal }) => {
  const requisites = useRequisitesStore((state) => state.requisites);
  const info = requisites[account.currencyId];
  return info?.SWIFT ? (
    <div
      className={twMerge(
        'flex h-full w-full flex-col items-center justify-center gap-1',
        isModal && 'mt-[-16px]',
      )}
    >
      {isModal ? (
        <div className="mb-[44px] flex w-full justify-center pt-[72px]">
          <div className="flex max-w-[300px] flex-col items-center gap-4 p-2">
            <CurrencyIcon ticker={account.code} size="xl" />
            <h1 className="text-center heading-m">{`${account.code} account details`}</h1>
            <div className="rounded-xl bg-base-500 p-3">
              <CopyArea
                className="max-w-[120px] gap-x-2 [&_svg]:size-6"
                copyText={getAccountInfoForCopy(info.SWIFT)}
                iconPosition="right"
                successCopyText="Details copied"
              >
                <Typography variant="16-r">Copy all</Typography>
              </CopyArea>
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-2 flex w-full items-center justify-between p-4">
          <p className="text-base-100 typography-14r">Account details</p>
          <CopyArea
            className="max-w-[78px] gap-x-2 [&_svg]:size-4"
            copyText={getAccountInfoForCopy(info.SWIFT)}
            iconPosition="right"
            successCopyText="Details copied"
          >
            <Typography variant="14-r">Copy all</Typography>
          </CopyArea>
        </div>
      )}

      <div className="flex w-full flex-col items-center gap-1">
        <ScreenCard
          size="sm"
          className={twMerge([
            'mt-1 flex flex-col gap-y-6 px-5 py-3 md:p-6',
            isModal && 'gap-y-3',
          ])}
        >
          {!!info.SWIFT && (
            <>
              <InfoItem
                title="Company name"
                value={info.SWIFT.beneficiary.name}
              />
              <InfoItem
                title="Company address"
                value={info.SWIFT.beneficiary.address}
              />
              <InfoItem
                title="Account number"
                value={info.SWIFT.beneficiaryBank.account}
              />
              <InfoItem
                title="SWIFT code"
                value={info.SWIFT.beneficiaryBank.code}
              />
              <InfoItem title="Bank" value={info.SWIFT.beneficiaryBank.name} />
            </>
          )}
        </ScreenCard>
        {!!info.SWIFT?.correspondentBanks?.length && (
          <ScreenCard>
            <InfoItem
              title="Intermediate bank"
              value={info.SWIFT.correspondentBanks[0].name}
            />
            <InfoItem
              title="Intermediate SWIFT code"
              value={info.SWIFT.correspondentBanks[0].code}
            />
          </ScreenCard>
        )}
        <ScreenCard>
          <div className="flex flex-col gap-y-1">
            <Typography variant="13-r" color="text-base-100">
              Payment details
            </Typography>

            <Typography variant="16-r">
              Provide your purpose of payment
            </Typography>
          </div>{' '}
        </ScreenCard>
      </div>
    </div>
  ) : (
    <div className="h-full w-full p-8">No requisites provided</div>
  );
};
